import { MemberStatus, ProjectMember, TeamRole } from '@/types';
import {
  AlertCircle,
  Building,
  Calendar,
  CheckCircle,
  Clock,
  Edit,
  Mail,
  MoreHorizontal,
  Phone,
  Shield,
  Trash2,
  User,
  XCircle,
} from 'lucide-react';
import React, { useState } from 'react';

interface TeamMemberCardProps {
  member: ProjectMember;
  onEdit: (member: ProjectMember) => void;
  onRemove: (member: ProjectMember) => void;
  onStatusToggle: (member: ProjectMember, newStatus: MemberStatus) => void;
  currentUserCanManage: boolean;
}

const TeamMemberCard: React.FC<TeamMemberCardProps> = ({
  member,
  onEdit,
  onRemove,
  onStatusToggle,
  currentUserCanManage,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const getStatusColor = (status: MemberStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: MemberStatus) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4" />;
      case 'inactive':
        return <XCircle className="h-4 w-4" />;
      case 'pending':
        return <Clock className="h-4 w-4" />;
      case 'suspended':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <XCircle className="h-4 w-4" />;
    }
  };

  const getRoleColor = (role: TeamRole) => {
    switch (role) {
      case 'project_manager':
        return 'bg-purple-100 text-purple-800';
      case 'architect':
        return 'bg-brand-100 text-brand-800';
      case 'engineer':
        return 'bg-indigo-100 text-indigo-800';
      case 'contractor':
        return 'bg-red-100 text-red-700';
      case 'site_supervisor':
        return 'bg-green-100 text-green-800';
      case 'client':
        return 'bg-pink-100 text-pink-800';
      case 'viewer':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatRole = (role: TeamRole) => {
    return role.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(date));
  };

  const formatLastActive = (date?: Date) => {
    if (!date) return 'Never';

    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    return formatDate(date);
  };

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  };

  const handleStatusToggle = () => {
    const newStatus: MemberStatus = member.status === 'active' ? 'inactive' : 'active';
    onStatusToggle(member, newStatus);
    setShowDropdown(false);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {member.user?.avatar ? (
                <img
                  src={member.user.avatar}
                  alt={`${member.user.firstName} ${member.user.lastName}`}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-lg font-semibold text-red-500">
                    {member.user ? getInitials(member.user.firstName, member.user.lastName) : '?'}
                  </span>
                </div>
              )}
            </div>

            {/* Member Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {member.user
                    ? `${member.user.firstName} ${member.user.lastName}`
                    : 'Unknown User'}
                </h3>
                <span
                  className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusColor(member.status)}`}
                >
                  {getStatusIcon(member.status)}
                  <span className="ml-1">
                    {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                  </span>
                </span>
              </div>

              <div className="mt-1 flex items-center space-x-4">
                <span
                  className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getRoleColor(member.role)}`}
                >
                  <Shield className="h-3 w-3 mr-1" />
                  {formatRole(member.role)}
                </span>
                {member.user?.title && (
                  <span className="text-sm text-gray-600 flex items-center">
                    <User className="h-4 w-4 mr-1" />
                    {member.user.title}
                  </span>
                )}
              </div>

              {member.user?.company && (
                <div className="mt-2 flex items-center text-sm text-gray-600">
                  <Building className="h-4 w-4 mr-1" />
                  {member.user.company}
                </div>
              )}

              <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                <span className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Joined {formatDate(member.assignedAt)}
                </span>
                <span className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  Active {formatLastActive(member.lastActive)}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          {currentUserCanManage && (
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <MoreHorizontal className="h-5 w-5" />
              </button>

              {showDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        onEdit(member);
                        setShowDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Member
                    </button>

                    <button
                      onClick={handleStatusToggle}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      {member.status === 'active' ? (
                        <>
                          <XCircle className="h-4 w-4 mr-2" />
                          Deactivate
                        </>
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Activate
                        </>
                      )}
                    </button>

                    <div className="border-t border-gray-100"></div>

                    <button
                      onClick={() => {
                        onRemove(member);
                        setShowDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove from Project
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Contact Info */}
      {member.user && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {member.user.email && (
                <a
                  href={`mailto:${member.user.email}`}
                  className="flex items-center text-sm text-gray-600 hover:text-red-500 transition-colors"
                >
                  <Mail className="h-4 w-4 mr-1" />
                  {member.user.email}
                </a>
              )}
              {member.user.phone && (
                <a
                  href={`tel:${member.user.phone}`}
                  className="flex items-center text-sm text-gray-600 hover:text-red-500 transition-colors"
                >
                  <Phone className="h-4 w-4 mr-1" />
                  {member.user.phone}
                </a>
              )}
            </div>

            {/* Permissions count */}
            <div className="text-sm text-gray-500">{member.permissions.length} permissions</div>
          </div>
        </div>
      )}

      {/* Expertise Tags */}
      {member.user?.expertise && member.user.expertise.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex flex-wrap gap-2">
            {member.user.expertise.slice(0, 3).map((skill, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-brand-50 text-brand-700"
              >
                {skill}
              </span>
            ))}
            {member.user.expertise.length > 3 && (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600">
                +{member.user.expertise.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showDropdown && <div className="fixed inset-0 z-0" onClick={() => setShowDropdown(false)} />}
    </div>
  );
};

export default TeamMemberCard;
